import React, { useContext } from "react"
import { Div, Text } from "atomize"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faBoxArchive,
	faCamera,
	faLayerGroup,
	faLocationDot,
	faRightToBracket,
	faTableCellsLarge,
	faTrashCan,
	faUsers,
} from "@fortawesome/free-solid-svg-icons"
import Card from "../atoms/Card"
import Tile from "../atoms/Tile"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { isArchivedSchool, isPrimarySchool, roleOf } from "../utils/school.utils"
import { managesSchool, teachesInSchool } from "../utils/func.utils"
import SchoolAvatar from "./SchoolAvatar"

// Il luogo: nome, indirizzo, campi. Prezzi e metodi di pagamento non sono
// del circolo ma del maestro che ci lavora, e stanno nella scheda accanto.
const ROLE_SUBTITLES = {
	owner: "Il circolo è tuo",
	manager: "Lo gestisci tu",
	viewer: "Vedi le lezioni di tutti",
	custom: "Hai qualche permesso in più",
	colleague: "Qui insegni soltanto",
}

function SchoolCard() {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { schools, getSelectedSchool, selectedSchool, canInviteTo, canEditSchool } =
		useContext(schoolContext)
	const { getSport } = useContext(sharedDataContext)

	const school = getSelectedSchool()
	if (!school) return null

	const canInvite = canInviteTo(selectedSchool)
	const canEdit = canEditSchool(selectedSchool)
	// Gestire un circolo non è possederlo: chi lo gestisce ne cambia i campi
	// e le lezioni, ma chi fa cosa lo decide e chiuderlo può solo chi l'ha
	// creato. Dirgli "è tuo" sarebbe una promessa che poi la schermata dei
	// dati smentisce.
	const role = roleOf(coach, school, selectedSchool)
	const courts = school.courts ?? {}
	const courtsCount = Object.values(courts).reduce(
		(total, sportCourts) => total + Object.keys(sportCourts).length,
		0,
	)
	// l'elenco dei maestri arriva come array di riferimenti e diventa un
	// dizionario quando il provider li risolve. Si contano i maestri, cioè
	// chi insegna: un direttore che non scende in campo è nel circolo, ma
	// maestro no. Finché sono riferimenti non si sa, e si contano tutti.
	const teachesHere = teachesInSchool(coach, selectedSchool)
	const schoolCoaches = Array.isArray(school.coaches)
		? null
		: Object.values(school.coaches ?? {}).filter(schoolCoach =>
				teachesInSchool(schoolCoach, selectedSchool),
			)
	const coachesCount = schoolCoaches
		? schoolCoaches.length
		: (school.coaches ?? []).length || 1
	const coachesValue =
		coachesCount === 0
			? "Nessuno"
			: coachesCount === 1 && teachesHere
				? "Solo tu"
				: coachesCount === 1
					? "1 maestro"
					: `${coachesCount} maestri`

	const sportNames = Object.keys(courts)
		.map(sportID => getSport(sportID)?.sportName ?? sportID)
		.join(", ")

	const open = section => Modal.open("schoolSection", section)

	// Principale e archiviati hanno senso da due circoli in su: con uno solo
	// non c'è niente da mettere in ordine.
	const hasManySchools = Object.keys(schools ?? {}).length > 1
	const isArchived = isArchivedSchool(coach, selectedSchool)
	const isPrimary = hasManySchools && isPrimarySchool(coach, selectedSchool)

	return (
		<Card bg={theme.colors.surface}>
			{/* Un circolo archiviato lo si guarda per lo storico: va detto,
			    perché per il resto è un circolo come gli altri — ci si
			    possono anche segnare lezioni. */}
			{isArchived && (
				<Div
					d="flex"
					align="center"
					justify="space-between"
					flexWrap="wrap"
					p="0.6rem 0.75rem"
					m={{ b: "1rem" }}
					rounded="12px"
					bg={theme.colors.background}
					style={{ gap: "0.5rem" }}>
					<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
						<FontAwesomeIcon icon={faBoxArchive} color={theme.colors.text_light} />
						<Text textSize="caption" textColor={theme.colors.text_light}>
							Archiviato: lo stai riguardando per lo storico
						</Text>
					</Div>
					<Text
						textSize="caption"
						textWeight="bold"
						textColor={theme.colors.primary}
						cursor="pointer"
						onClick={() => Modal.open("mySchools")}>
						I tuoi circoli
					</Text>
				</Div>
			)}

			<Div d="flex" align="center" m={{ b: "1rem" }} style={{ gap: "0.75rem" }}>
				{/* Il logo, se il circolo ne ha uno; se no la puntina del luogo.
				    A chi può cambiarlo, toccandolo si apre il foglio del logo
				    (PictureModal), e lo dice la macchina fotografica
				    nell'angolo; agli altri apre i dati del circolo, da guardare. */}
				<motion.button
					type="button"
					aria-label={canEdit ? "Cambia il logo del circolo" : "Dati del circolo"}
					whileTap={{ scale: 0.94 }}
					onClick={() =>
						canEdit ? Modal.open("picture", { kind: "school" }) : open("dati")
					}
					style={{
						position: "relative",
						flexShrink: 0,
						width: "48px",
						height: "48px",
						padding: 0,
						border: 0,
						borderRadius: "50%",
						background: "transparent",
						cursor: "pointer",
					}}>
					{school.logo ? (
						<SchoolAvatar school={school} size={48} />
					) : (
						<Div
							w="48px"
							h="48px"
							rounded="circle"
							bg={theme.colors.gunmetal_fill}
							d="flex"
							align="center"
							justify="center">
							<FontAwesomeIcon icon={faLocationDot} color="white" size="lg" />
						</Div>
					)}
					{canEdit && (
						<span
							aria-hidden="true"
							style={{
								position: "absolute",
								right: "-4px",
								bottom: "-4px",
								width: "22px",
								height: "22px",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								background: theme.colors.primary,
								border: `2px solid ${theme.colors.surface}`,
								boxSizing: "border-box",
							}}>
							<FontAwesomeIcon icon={faCamera} color="white" style={{ fontSize: "9px" }} />
						</span>
					)}
				</motion.button>
				<Div>
					<Text textWeight="bold" textSize="subheader">
						{school.schoolName}
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{teachesHere
							? ROLE_SUBTITLES[role]
							: role === "colleague"
								? "Qui non insegni"
								: `${ROLE_SUBTITLES[role]} · non ci insegni`}
						{isPrimary && " · è il principale"}
					</Text>
				</Div>
			</Div>

			<Div d="flex" flexWrap="wrap" style={{ gap: "0.75rem" }}>
				<Tile
					icon={faLocationDot}
					label="Dati"
					value={school.address || "Da completare"}
					hint={school.mapsName || null}
					color={theme.colors.gunmetal_fill}
					onClick={() => open("dati")}
				/>
				<Tile
					icon={faTableCellsLarge}
					label="Campi"
					value={
						courtsCount === 0
							? "Nessuno"
							: courtsCount === 1
								? "1 campo"
								: `${courtsCount} campi`
					}
					hint={sportNames || null}
					color={theme.colors.clay}
					onClick={() => open("campi")}
				/>
				<Tile
					icon={faUsers}
					label="Maestri"
					value={coachesValue}
					hint={canInvite ? "Fai entrare un collega" : null}
					color={theme.colors.gunmetal_fill}
					onClick={() => Modal.open("schoolTeam")}
				/>
				{/* chi gestisce il circolo le vede tutte; un maestro le sue tolte
				    da altri */}
				<Tile
					icon={faTrashCan}
					label="Lezioni eliminate"
					value={managesSchool(coach, selectedSchool) ? "Del circolo" : "Le tue"}
					hint="Chi le ha tolte, e cosa c'era"
					color={theme.colors.red}
					onClick={() => Modal.open("lessonDeletions")}
				/>
			</Div>

			{/* questi non parlano di questo circolo ma degli altri: stanno
			    fuori dai tasselli apposta */}
			{hasManySchools && (
				<Div
					d="flex"
					align="center"
					m={{ t: "1rem" }}
					cursor="pointer"
					style={{ gap: "0.5rem" }}
					onClick={() => Modal.open("mySchools")}>
					<FontAwesomeIcon icon={faLayerGroup} color={theme.colors.primary} size="sm" />
					<Text textSize="caption" textColor={theme.colors.primary}>
						I tuoi circoli: principale e archiviati
					</Text>
				</Div>
			)}
			<Div
				d="flex"
				align="center"
				m={{ t: hasManySchools ? "0.5rem" : "1rem" }}
				cursor="pointer"
				style={{ gap: "0.5rem" }}
				onClick={() => Modal.open("joinSchool")}>
				<FontAwesomeIcon icon={faRightToBracket} color={theme.colors.primary} size="sm" />
				<Text textSize="caption" textColor={theme.colors.primary}>
					Entra in un altro circolo
				</Text>
			</Div>
			<Div
				d="flex"
				align="center"
				m={{ t: "0.5rem" }}
				cursor="pointer"
				style={{ gap: "0.5rem" }}
				onClick={() => Modal.open("newSchool")}>
				<FontAwesomeIcon icon={faLocationDot} color={theme.colors.primary} size="sm" />
				<Text textSize="caption" textColor={theme.colors.primary}>
					Crea un altro circolo
				</Text>
			</Div>
		</Card>
	)
}

export default SchoolCard
